'use strict';
//***********************************************************************************
//***********************************************************************************
//**** cn_svg_tool_selection  : A SVG tool to manipulate walls and openings
//***********************************************************************************
//***********************************************************************************
import { cn_balcony_type } from '../model/cn_balcony_type';
import { cn_fence_type } from '../model/cn_fence_type';
import { cn_space } from '../model/cn_space';
import { cn_vertex } from '../model/cn_vertex';
import { CN_WALL_COSINE_SHAPE_THRESHOLD } from '../model/cn_model_constants';
import { CN_INNER, CN_MIDDLE, CN_OUTER, cn_wall } from '../model/cn_wall';
import { cn_wall_type } from '../model/cn_wall_type';
import { cn_add, cn_cart, cn_clone, cn_dist, cn_dot, cn_intersect_line, cn_manhattan_distance, cn_mul, cn_normalize, cn_sub } from '../utils/cn_utilities';
import { cn_edit_box } from './cn_edit_box';
import { cn_notification_input, cn_number_input, SEVERITY_NOTIFICATION_ERROR } from './cn_inputs';
import { cn_pastille } from './cn_pastille';
import { cn_snap } from './cn_snap';
import { cn_space_handler } from './cn_space_handler';
import { cn_svg_tool_edition } from './cn_svg_tool_edition';
import { cn_type_pastille } from './cn_type_pastille';
import { cn_measure } from './cn_measure';
import { cn_angle_sensitive_area } from './cn_camera';
import { cn_plugin_option } from '../utils/cn_plugin_option';
import { cn_space_dummy } from '../model/cn_space_dummy';
import { cn_space_dummy_handler } from './cn_space_dummy_handler';
import { cn_wall_handler } from './cn_wall_handler';

export const MIN_WALL_WIDTH = 0.1;

class cnw_measure {
    constructor(p0, p1, direction) {
        this.points = [p0, p1];
        this.direction = direction;
        this.selected = false;
        this.box_position = [0, 0];
        this.distance = cn_dist(this.points[0], this.points[1]);
    }

    draw(camera, editable) {
        return camera.draw_measure(this.points[0], this.points[1], (editable) ? this.box_position : null, this.selected);
    }

    clear_move() {
        this.selected = false;
    }

    mouseover(pos) {
        this.selected = (Math.abs(pos[0] - this.box_position[0]) < 30 && Math.abs(pos[1] - this.box_position[1]) < 10);
        return this.selected;
    }
}

//***********************************************************************************
//**** Wall edition
//***********************************************************************************

const SPLIT_THRESHOLD = Math.sin(20 * Math.PI / 180);

export class cn_svg_tool_wall_edition extends cn_svg_tool_edition {
    constructor(map, filters = ['wall', 'balcony', 'fence']) {
        super(map);

        this._initial_filters = filters;
        this._filters = ['wall', 'balcony', 'fence'];

        this._mouse_position = [0, 0];

        this._selected_walls = [];
        this._selected_vertices = [];

        this._need_update_implied_selection = true;

        this._svg = '';

        this._drag_code = 0;
        this.first_drag = false;

        this._selected_space = null;
        this._mouseover_space = false;
        this._mouse_wall_measures = null;
        this.angle_sensitive_areas = [];

        /** @type{function} */
        this._pending_changes = null;

        this._space_diagonals = false;

        this._edit_box = null;

        this.initial_snap_lines = [];
    }

    //***********************************************************************************
    //**** Selection callback
    //***********************************************************************************
    on_selection_change() {
        this._mouseover_measure = null;

        this._edit_box = null;
        this._selected_walls = [];
        this._selected_vertices = [];
        this._mouseover_element = null;
        this._implied_vertices = [];
        this._implied_walls = [];
        this._mouse_wall_measures = null;
        this._selection_wall_measures = null;
        this._need_update_implied_selection = true;
        this._space_diagonals = false;
        this._mouseover_angle = null;

        //*** end if selection is not excluusively walls and vertices */
        var selection = this._controller.get_selection();
        if (selection.length == 0) return false;

        //*** selection made only of spaces ? */
        if (!selection.some(elt => elt.constructor != cn_space)) {
            this._edit_box = new cn_space_handler(selection, this.get_svg_map());
            this._edit_box.on('diagonals_mode', (v) => {
                this._space_diagonals = v;
                this._map.refresh();
            });
            return true;
        }

        //*** selection made only of space dummies ? */
        if (!selection.some(elt => elt.constructor != cn_space_dummy)) {
            this._edit_box = new cn_space_dummy_handler(selection, this.get_svg_map());
            return true;
        }

        if (selection.some(elt => elt.constructor != cn_wall && elt.constructor != cn_vertex))
            return false;

        var coherent = true;
        var wall_type_constructor = null;
        for (var i in selection) {
            if (selection[i].constructor == cn_wall) {
                if (this.check_filters(selection[i])) {
                    this._selected_walls.push(selection[i]);
                    if (wall_type_constructor == null)
                        wall_type_constructor = selection[i].wall_type.constructor;
                    else if (wall_type_constructor != selection[i].wall_type.constructor)
                        coherent = false;
                }
            } else if (selection[i].constructor == cn_vertex)
                this._selected_vertices.push(selection[i]);
        }
        this._mouseover_element = null;

        const removable_vertices = this._selected_vertices.filter(v => v.walls.length == 2);

        //*** Create edit box */
        if (this._selected_walls.length > 0) {
            const map = this.get_svg_map();
            let wall_handler = new cn_wall_handler(this._selected_walls, map);
            this._edit_box = wall_handler;
        } else if (removable_vertices.length > 0) {
            this._edit_box = new cn_edit_box(this, removable_vertices, this._map.get_readonly());
        }
        return true;
    }

    //***********************************************************************************
    //**** filters
    //***********************************************************************************
    check_filters(wall) {
        if (wall.balcony)
            return (this._filters.indexOf('balcony') >= 0);
        if (wall.fence)
            return (this._filters.indexOf('fence') >= 0);
        return (this._filters.indexOf('wall') >= 0);
    }

    //***********************************************************************************
    //**** Clear selection
    //***********************************************************************************
    clear_selection() {
    }

    //***********************************************************************************
    //**** Open / Close tool
    //***********************************************************************************
    open_tool() {
        super.open_tool();
        this.clear_selection();
    }

    close_tool() {
        super.close_tool();
        this.clear_selection();
        this._scene.update_deep();
    }

    /**
     * Sets space diaglonal mode
     * @param {boolean} v
     */
    set_space_diagonals(v) {
        this._space_diagonals = v;
    }

    /**
     * returns space diaglonal mode
     * @returns {boolean}
     */
    get_space_diagonals() {
        return this._space_diagonals;
    }

    //***********************************************************************************
    //**** Specific selection methods
    //***********************************************************************************
    //*** Returns selection
    get_selected_walls() {
        const selection = this._controller.get_selection();
        const sel = [];
        for (let i in selection) {
            if (selection[i].constructor === cn_wall) {
                sel.push(selection[i]);
            }
        }
        this._selected_walls = sel;
        return this._selected_walls;
    }

    //*** Returns selected vertices
    get_selected_vertices() {
        return this._selected_vertices;
    }

    //*** Returns all selected wall types
    get_selected_wall_types() {
        var wall_types = [];
        for (var i in this._selected_walls) {
            var ot = this._selected_walls[i].wall_type;
            if (wall_types.indexOf(ot) < 0)
                wall_types.push(ot);
        }
        return wall_types;
    }

    //*** Select all inner and / or outer walls
    select_walls(inner_walls, outer_walls) {
        this.clear_selection();
        this._selection = [];
        for (var i in this._scene.walls) {
            if (this._scene.walls[i].balcony) continue;
            if (this._scene.walls[i].fence) continue;
            if (inner_walls && outer_walls)
                this._selection.push(this._scene.walls[i]);
            else if (inner_walls) {
                if (this._scene.walls[i].spaces[0].has_roof && this._scene.walls[i].spaces[1].has_roof)
                    this._selection.push(this._scene.walls[i]);
            } else if (outer_walls) {
                if (this._scene.walls[i].spaces[0].has_roof != this._scene.walls[i].spaces[1].has_roof)
                    this._selection.push(this._scene.walls[i]);
            }
        }
        this.call('selection_change');
    }

    //*** Select all balconies
    select_balconies() {
        this.clear_selection();
        this._selection = [];
        for (var i in this._scene.walls) {
            if (!this._scene.walls[i].balcony) continue;
            this._selection.push(this._scene.walls[i]);
        }
        this.call('selection_change');
    }

    //*** Set wall types for selection
    set_selection_wall_type(wt) {
        var walls = this.get_selected_walls();
        if (walls.length == 0) return;

        var scene = this._scene;
        var balcony = (wt.constructor == cn_balcony_type);

        var transaction_name = (balcony) ? 'Modification du type de balcon' : 'Modification du type de mur';
        this.push_transaction(transaction_name, '', () => {
            scene.full_update();
        });

        for (var i in walls) {
            this.push_item_set(walls[i], ['balcony', 'wall_type']);
            walls[i].balcony = balcony;
            walls[i].wall_type = wt;
        }
        this._scene.update();
        this._scene.update_deep();
    }

    //*** return axis list for selection
    get_current_axis_list() {
        var axis_list = [];
        var walls = this.get_selected_walls();
        for (var i in walls) {
            var axis = walls[i].axis;
            if (axis_list.indexOf(axis) < 0)
                axis_list.push(axis);
        }

        return axis_list;
    }

    //*** Set wall axis for selection
    set_selection_wall_axis(axis) {
        var walls = this.get_selected_walls();
        if (walls.length == 0) return;
        this._scene.set_walls_axis(walls, axis, this._scene.building.transaction_manager);
    }

    //*** walled when some walls are updated
    update_walls() {
        this._scene.update();
        this._scene.update_deep();
    }

    _update_measures() {

        this._measures = [];
        const obj = this;
        function add_measures(wall, vertex) {
            const ms = wall.get_measures_from_vertex(vertex);
            ms.forEach(m => {
                obj._measures.push(new cnw_measure(m.points[0], m.points[1], m.direction));
            });
        }
        this._implied_walls.forEach(w => {
            //*** measures from supported vertices */
            w.supported_vertices
                .filter(v => v.support_wall_side < 2 && v.walls.length == 1 && !this._implied_walls.includes(v.walls[0]))
                .forEach(v => add_measures(v.walls[0], v));

            //*** measures from wall vertices that are supproted */
            w.vertices
                .filter(v => v.support_wall && !this._implied_walls.includes(v.support_wall))
                .forEach(v => add_measures(v.support_wall, v));

            //*** measures from shared vertices */
            w.vertices
                .filter(v => v.walls.length == 2)
                .forEach(v => {
                    const vi = v.walls.indexOf(w);
                    const other_wall = v.walls[1 - vi];
                    if (!this._implied_walls.includes(other_wall))
                        add_measures(other_wall, v);
                });
        });

        //*** Maybe measures to a single vertex */
        if (this._selected_vertices.length == 1 &&
            this._selected_walls.length == 0 &&
            this._selected_vertices[0].is_free()) {
            add_measures(this._selected_vertices[0].walls[0], this._selected_vertices[0]);
        }
    }
    //***********************************************************************************
    //**** Draws  specific svg for the tool. Returns svg string
    //***********************************************************************************
    draw(camera) {
        //*** What are the walls implied in selection due to vertex selection ?
        this.update_implied_selection();

        var html = '';

        //*** draw mouse wall measures */
        if (this._mouse_wall_measures) {
            for (var i in this._mouse_wall_measures.measures) {
                var mwm = this._mouse_wall_measures.measures[i];
                html += camera.draw_measure(mwm[0], mwm[1], null, false, 0);
            }
        }

        var selection = this._controller.get_selection();
        var can_translate = this.can_translate();


        //*** Draw movable measures */
        this._measures.forEach(m => html += m.draw(camera, can_translate));

        //*** draw selection wall measures */
        if (this._selection_wall_measures && selection.indexOf(this._selection_wall_measures.wall) >= 0) {
            this.update_wall_measures(this._selection_wall_measures);
            for (var i in this._selection_wall_measures.measures) {
                var mwm = this._selection_wall_measures.measures[i];
                if (can_translate) {
                    var selected = (this._mouseover_selection_measure && this._mouseover_selection_measure.wall == this._selection_wall_measures.wall && this._mouseover_selection_measure.index == i);
                    html += camera.draw_measure(mwm[0], mwm[1], mwm[2], selected, 0);

                } else
                    html += camera.draw_measure(mwm[0], mwm[1], null, false, 0);
            }
        }

        //*** Draw measures of implied walls
        if (this._implied_wall_measures.length == 0) {
            this._implied_wall_measures = [];
            this._implied_walls.forEach(w => {
                w.space_segments.forEach(ss => {
                    for (var ss0 = ss; ;) {
                        let s = ss0.get_previous();
                        if (cn_manhattan_distance(s.direction, ss.direction) > 0.001) break;
                        ss0 = s;
                    }
                    for (var ss1 = ss; ;) {
                        let s = ss1.get_next();
                        if (cn_manhattan_distance(s.direction, ss.direction) > 0.001) break;
                        ss1 = s;
                    }
                    const m = new cn_measure(this, ss0.points[0], ss1.points[1]);
                    this._implied_wall_measures.push(m);
                    m["element_around"] = [null, null];
                    if (ss.wall_side < 2) {
                        for (var na = 0; na < 2; na++) {
                            let other_ss = (na == 0) ? ss0.get_previous() : ss1.get_next();
                            if (other_ss && other_ss.wall) {
                                if (other_ss.wall == w) {
                                    if (other_ss.wall_side >= 2) {
                                        const vertex = w.vertices[other_ss.wall_side - 2];
                                        if (vertex.is_free())
                                            m["element_around"][na] = vertex;
                                    }
                                }
                                else if (other_ss.wall_side < 2 && !other_ss.wall.locked && Math.abs(cn_dot(other_ss.wall.bounds.direction, w.bounds.direction)) < 0.5)
                                    m["element_around"][na] = other_ss.wall;
                            }
                        }
                    }
                    if (m["element_around"][0] && m["element_around"][1])
                        m.selectable = cn_measure.selectable.left_and_right;
                    else if (m["element_around"][0])
                        m.selectable = cn_measure.selectable.left;
                    else if (m["element_around"][1])
                        m.selectable = cn_measure.selectable.right;
                    else
                        m.selectable = cn_measure.selectable.none;

                    m.set_measure = (value, control) => this._set_measure(m, value, control);
                });
            });
        }
        this._implied_wall_measures.forEach(m => {
            html += m.draw_with_highlight(camera);
            if (m._mouseover > 0)
                html += m["element_around"][m._mouseover - 1].draw(camera, ["selected"]);
        });

        //*** Draw measures of selected spaces
        for (var i in selection) {
            if (selection[i].constructor != cn_space) continue;
            this._selected_space = selection[i];
            if (!this._selected_space.outside && this._space_diagonals) {
                html += selection[i].measure.draw(camera);
                html += this._selected_space.draw_main_door_selected_space(camera);
            }
        }


        /** Draw wall that will move on angle setting */
        if (this._mouseover_angle)
            html += this._mouseover_angle["wall"].draw(camera, ["selected"]);

        //*** Draw angles of selected vertex
        //if (this._selected_vertices.length == 1 && this._selected_walls.length == 0 && this._selected_vertices[0].walls.length > 1)
        this.angle_sensitive_areas = [];
        if (this._implied_vertices.length < 10) {
            this._implied_vertices.forEach(v => {
                if (!v["angle_sensitive_area"])
                    v["angle_sensitive_area"] = [];
                html += v.draw_angles(camera, v["angle_sensitive_area"]);
                this.angle_sensitive_areas = this.angle_sensitive_areas.concat(v["angle_sensitive_area"]);
            });
        }

        html += this._svg;

        if (this._edit_box) html += this._edit_box.draw(camera);
        return html;
    }

    _set_measure(measure, value, control) {
        const moving_element = measure["element_around"][control - 1];
        if (!moving_element) return;

        //*** Sets selection to what is actually moving */
        const stored_selected_vertices = this._selected_vertices;
        const stored_selected_walls = this._selected_walls;
        this._selected_vertices = [];
        this._selected_walls = [];
        if (moving_element.constructor == cn_vertex)
            this._selected_vertices = [moving_element];
        else if (moving_element.constructor == cn_wall)
            this._selected_walls = [moving_element];
        this.update_implied_selection(true);

        //*** actually moves element
        let dir = cn_sub(measure.vertices[1], measure.vertices[0]);
        const l = cn_normalize(dir);
        dir = cn_mul(dir, value - l);
        if (control == 1) dir = cn_mul(dir, -1);
        if (this._selected_vertices.length) {
            this.push_transaction('Déplacement de sommet');
            this.push_item_set(this._selected_vertices[0], "position");
            this._selected_vertices[0].position = cn_add(this._selected_vertices[0].position, dir);
        }
        else if (this._selected_walls.length)
            this.apply_wall_translation(dir, 'Déplacement de murs');
        this.finalize_pending_changes(true);

        //*** Restore selection */
        this._selected_vertices = stored_selected_vertices;
        this._selected_walls = stored_selected_walls;
        this.update_implied_selection(true);

        this._update_measures();

        this._map.refresh();
    }

    //***********************************************************************************
    //**** update implied elements
    //***********************************************************************************
    update_implied_selection(force = false) {
        if (!force && !this._need_update_implied_selection) return;
        this._need_update_implied_selection = false;

        function add_element(e, list) {
            if (!list.includes(e)) list.push(e);
        }

        //*** Implied vertices are all the vertices that will move on a translation. : the selected vertices + the vertices of selected walls */
        this._implied_vertices = [];
        this._implied_vertices = this._implied_vertices.concat(this._selected_vertices);
        for (var i in this._selected_walls) {
            var wall = this._selected_walls[i];
            add_element(wall.vertices[0], this._implied_vertices);
            add_element(wall.vertices[1], this._implied_vertices);
        }
        this._implied_vertices.forEach(v => v["angle_sensitive_area"] = []);

        //*** implied walls are the walls that will move on a translation : all walls for which both vertices are implied. */
        //*** flexible walls are walls where only one vertex will move. */
        this._implied_walls = [];
        this._implied_wall_measures = [];
        this._flexible_walls = [];
        for (var i in this._implied_vertices) {
            var vertex = this._implied_vertices[i];
            for (var j in vertex.walls) {
                var wall = vertex.walls[j];
                if (this._implied_vertices.includes(wall.other_vertex(vertex)))
                    add_element(wall, this._implied_walls);
                else
                    add_element(wall, this._flexible_walls);
            }
        }
        this._managed_walls = [...this._implied_walls, ...this._flexible_walls];

        //*** Connectd walls are all the fixed walls that may need to be updated */
        this._connected_walls = [];

        //*** suported vertices are supported by implied or flexible walls, but come from a wall that does not move */
        this._supported_vertices = [];
        [...this._implied_walls, ...this._flexible_walls].forEach(w => {
            w.supported_vertices.filter(v => v.walls.length == 1 && !this._managed_walls.includes(v.walls[0])).forEach(v => {
                v["initial_support_wall"] = w;
                this._supported_vertices.push(v);
                add_element(v.walls[0], this._connected_walls);
            });
        });

        this._flexible_walls.forEach(w => {
            w.vertices.filter(v => !this._implied_vertices.includes(v)).forEach(v => {
                if (v.walls.length == 2) {
                    const ww = (v.walls[0] == w) ? v.walls[1] : v.walls[0];
                    if (!this._managed_walls.includes(ww))
                        add_element(ww, this._connected_walls);
                }
                else if (v.support_wall && !this._managed_walls.includes(v.support_wall))
                    add_element(v.support_wall, this._connected_walls);
            });
        });

        //*** sliding vertices are vertices belonging to a flexible or implied wall, but supported by a fixed wall. */
        this._sliding_vertices = [];
        this._implied_walls.forEach(w => {
            w.vertices.forEach(v => {
                if (v.support_wall && v.walls.length == 1 && !this._managed_walls.includes(v.support_wall)) {
                    v["initial_support_wall"] = v.support_wall;
                    this._sliding_vertices.push(v);
                }
            });
        });

        this._managed_walls = this._managed_walls.concat(this._connected_walls);

        this._update_measures();
    }

    //***********************************************************************************
    //**** update selection
    //***********************************************************************************
    update_selection(clear_selection) {

        //*** Clear selection if needed
        if (clear_selection) {
            this._selection = [];
            this._selected_vertices = [];
            this._selected_walls = [];
            this._selected_spaces = [];
            this._selected_openings = [];
        }
        this._need_update_implied_selection = true;

        //*** Add mouseover element to selection
        if (this._mouseover_element) {
            var index = this._selection.indexOf(this._mouseover_element);
            if (index >= 0)
                this._selection.splice(index, 1);
            else
                this._selection.push(this._mouseover_element);
            this._need_update_implied_selection = true;
            this._mouseover_element = null;
        }

        this.call('selection_change');
    }

    //***********************************************************************************
    //**** Angle click
    //***********************************************************************************

    /**
     *
     * @param {cn_angle_sensitive_area} sensitive_area
     */
    _call_angle_click(sensitive_area) {
        const obj = this;
        const input = new cn_number_input('Nouvel angle', Math.abs(sensitive_area.angles[1] - sensitive_area.angles[0]) * 180 / Math.PI, '°', 1);
        input.callback = () => {
            this._set_current_angle(input.value, sensitive_area)
            this._map.refresh();
        }
        this.call('number_input', input);
    }

    _set_current_angle(v, sensitive_area) {
        let fixedvertex = sensitive_area["vertex"];
        let wall = sensitive_area["wall"];
        let vertex = wall.other_vertex(fixedvertex);

        let other_wall = vertex.support_wall;
        if (!other_wall && vertex.walls.length > 1)
            other_wall = (vertex.walls[0] == wall) ? vertex.walls[1] : vertex.walls[0];
        let new_angle = (sensitive_area.angles[1] > sensitive_area.angles[0]) ? sensitive_area.angles[1] - v * Math.PI / 180 : sensitive_area.angles[1] + v * Math.PI / 180;
        let direction = cn_cart([1, new_angle]);
        let new_position = [0, 0];
        if (other_wall)
            new_position = cn_intersect_line(fixedvertex.position, direction, other_wall.vertices[0].position, other_wall.bounds.direction);
        else
            new_position = cn_add(fixedvertex.position, cn_mul(direction, cn_dist(fixedvertex.position, vertex.position)));

        if (new_position) {
            this.push_transaction("Modification d'angle");
            this.push_item_set(vertex, "position");
            vertex.position = new_position;
            this._scene.full_update();
            this.update_implied_selection(true);
            this._update_measures();
        }
        else
            this.call('notification_input', new cn_notification_input('Opération impossible', SEVERITY_NOTIFICATION_ERROR));
    }

    //***********************************************************************************
    //**** Measure click
    //***********************************************************************************

    _call_measure_click(v) {
        const obj = this;
        const input = new cn_number_input('Nouvelle longueur', v, 'm', 2, MIN_WALL_WIDTH);
        input.callback = () => {
            if (!this.set_current_measure(input.value)) {
                this.call('notification_input', new cn_notification_input('Opération impossible', SEVERITY_NOTIFICATION_ERROR));
            }
            this._map.refresh();
        }
        this.call('number_input', input);
    }

    set_current_measure(v1) {
        var scene = this._scene;

        if (this.can_translate()) {
            //*** Typical measure changed */
            if (this._mouseover_measure) {
                if (this.can_translate_walls()) {
                    const x = v1 - this._mouseover_measure.distance;
                    this.apply_wall_translation(cn_mul(this._mouseover_measure.direction, x), 'Déplacement de murs');
                    this.finalize_pending_changes(true);
                    this._update_measures();
                    return true;
                }

                var vertex = this._implied_vertices[0];
                var old_position = cn_clone(vertex.position);

                return true;
            }

            //*** wall measure clicked ?
            else if (this._mouseover_selection_measure) {
                // @ts-ignore
                var wall = this._mouseover_selection_measure.wall;
                var index = this._mouseover_selection_measure.index;
                if (this._selection_wall_measures && this._selection_wall_measures.wall == wall && this._selection_wall_measures.measures[index]) {
                    var direction = cn_sub(this._selection_wall_measures.measures[index][0], this._selection_wall_measures.measures[index][1]);
                    var dist = cn_normalize(direction);

                    var bres = this.apply_wall_translation(cn_mul(direction, v1 - dist), 'Modification de longueur de mur');
                    scene.update();
                    scene.update_deep();
                    return bres;
                }
                return false;
            }
        }

        //*** space measure changed */
        if (this._selected_space && !this._selected_space.outside && this._space_diagonals && this._mouseover_space) {
            this._mouseover_space = false;

            //*** Apply new measure */
            return this._selected_space.measure.set_measure(v1, this._building.transaction_manager);
        }
        this._scene.update();
        this._scene.update_deep();
        return true;
    }

    //***********************************************************************************
    //**** Mouse callbacks
    //***********************************************************************************
    clear_move() {
        this._mouseover_measure = null;
        this._mouseover_selection_measure = null;
        this._mouseover_space = false;
        this._mouse_wall_measures = [];
        this._mouseover_element = null;
        if (this._edit_box) this._edit_box.clear_move();
        this._implied_wall_measures.forEach(m => m.clear_move());
        this._mouseover_angle = null;
        this.angle_sensitive_areas.forEach(sa => sa.mouseover = false);
    }

    click(ev) {
        var obj = this;
        this._svg = '';

        if (this._edit_box && this._edit_box.click(ev)) return true;

        if (this._implied_wall_measures.some(m => m.click(ev)))
            return true;

        this.update_mouseover(ev);

        if (this._map.get_readonly()) {
            return false;
        }

        //*** measure clicked ?
        if (this._mouseover_measure) {
            this._call_measure_click(this._mouseover_measure.distance);
            return true;
        }

        if (this._mouseover_angle) {
            this._call_angle_click(this._mouseover_angle);
            return true;
        }

        //*** wall measure clicked ?
        if (this._mouseover_selection_measure) {
            //@ts-ignore
            var wall = this._mouseover_selection_measure.wall;
            var index = this._mouseover_selection_measure.index;
            if (this._selection_wall_measures && this._selection_wall_measures.wall == wall && this._selection_wall_measures.measures[index]) {
                var v = cn_dist(this._selection_wall_measures.measures[index][0], this._selection_wall_measures.measures[index][1]);
                this._call_measure_click(v);
                return true;
            }
        }

        //*** mouseover space clicked ?
        if (this._selected_space && !this._selected_space.outside && this._space_diagonals && this._mouseover_space) {
            if (this._selected_space.measure.diagonal_origin_selected()) {
                this.push_transaction(`Origine des diagonales d'une pièce`);
                this.push_item_set(this._selected_space.measure, 'diagonal_origin');
                this._selected_space.measure.click();
                return true;
            }

            var dist = this._selected_space.measure.click();
            if (typeof (dist) == 'number')
                this._call_measure_click(dist);
            return true;
        }

        this._selection_wall_measures = this._mouse_wall_measures;

        return false;
    }

    grab(ev) {

        if (this._edit_box && this._edit_box.grab(ev)) return true;

        //if (!this.move(ev)) return false;
        this.update_mouseover(ev);
        this._dragged_element = this._mouseover_element;
        if (this._dragged_element == null) return false;
        if (this._selected_vertices.indexOf(this._dragged_element) < 0 && this._selected_walls.indexOf(this._dragged_element) < 0) return false;
        this._mouse_position = cn_clone(ev.mouse_world);
        this._drag_code++;
        this.first_drag = true;

        this.initial_snap_lines = [];
        if (this.can_translate_vertex() && this._selected_vertices.length > 0) {
            let vertex = this._selected_vertices[0];
            this.initial_snap_lines = this._selected_vertices[0].walls.map(w => [cn_clone(vertex.position),cn_clone(w.bounds.direction)]);
        }
        return true;
    }

    drop(ev) {
        this._svg = '';
        if (this._edit_box && this._edit_box.drop(ev)) return true;

        this._dragged_element = null;
        //*** Manage end of drag
        this.finalize_pending_changes();

        return true;
    }

    move(ev) {
        this.clear_move();
        if (this._edit_box && this._edit_box.move(ev)) return true;
        return this.update_mouseover(ev);
    }

    drag(ev) {
        if (this._edit_box && this._edit_box.drag(ev)) return true;
        this.clear_move();
        var scene = this._scene;
        //this.push_transaction("Déplacement de murs",scene.ID + this._drag_code,() => {scene.update();scene.update_deep();});

        if (this._dragged_element == null) return false;

        this.drag_selection(ev);

        return true;
    }

    start_translation(ev) {
        if (this._initial_filters.indexOf('wall') < 0) return;
        this._mouse_position = cn_clone(ev.mouse_world);
        this._dragged_element = this._controller.find_element(ev.mouse_world, ev.camera.snap_world_distance);
    }

    translate(ev, offset) {
        if (this._initial_filters.indexOf('wall') < 0) return;
        this.drag_selection(ev);
    }

    finalize_translation(ev) {
        if (this._initial_filters.indexOf('wall') < 0) return;
        this._svg = '';
    }

    //***********************************************************************************
    //**** Finalize pending changes
    //***********************************************************************************
    finalize_pending_changes(force = false) {
        if (!force && this._pending_changes === null) return;
        if (this._pending_changes) this._pending_changes();
        this._scene.full_update();
        this.update_implied_selection(true);
        this._pending_changes = null;
        this._map.refresh();
    }

    //***********************************************************************************
    //**** Drag selection
    //***********************************************************************************
    drag_selection(ev) {
        var delta = cn_sub(ev.mouse_world, this._mouse_position);
        var actual_delta = cn_clone(delta);

        var scene = this._scene;
        this.update_implied_selection();

        //*** One single vertex selected : try to snap on existing walls
        var selection = this._controller.get_selection();
        if (selection.length == 1 && selection[0].constructor == cn_vertex) {
            var vertex = selection[0];

            //if (vertex.walls.some(w => w.locked)) return false;

            //*** First drag : we create the transaction */
            if (this.first_drag) {
                this.first_drag = false;
                this.push_transaction('Déplacement de sommet', scene.ID + this._drag_code);

                //*** if the moved vertex is supported, we detach the vertex */
                if (vertex.support_wall)
                    vertex.set_support_wall(null, -1, this._scene.building.transaction_manager);
            }

            //*** Move the vertex */
            return this._move_vertex(ev, vertex);
        }

        //*** move walls
        else if (this.can_translate_walls()) {
            var x = cn_dot(actual_delta, this._dragged_element.bounds.normal);
            actual_delta = cn_mul(this._dragged_element.bounds.normal, x);

            if (this.first_drag) {
                this.first_drag = false;
                this.push_transaction('Déplacement de murs', scene.ID + this._drag_code);
            }

            if (this.apply_wall_translation(actual_delta, '', ev.camera))
                this._mouse_position = cn_add(this._mouse_position, actual_delta);
            this._pending_changes = () => { };
            return true;
        }

        return true;
    }

    //***********************************************************************************
    //**** Moves one  vertex
    //***********************************************************************************
    _move_vertex(ev, vertex) {
        if (vertex.walls.length == 0) return;

        const res = (vertex.walls.length == 1) ? this._move_single_vertex(ev, vertex) : this._move_connected_vertex(ev, vertex);
        if (!res) return;

        this._scene.scene_analyser.update_tmp_walls(this._managed_walls);

        this._supported_vertices.forEach(v => {
            if (v != vertex)
                v.check_support_wall(v["initial_support_wall"], this._scene.building.transaction_manager);
        });

        this._scene.scene_analyser.update_tmp_walls(this._connected_walls);

        if (vertex.support_wall) vertex.support_wall.update_openings();

        this._update_measures();

        return res;
    }

    //***********************************************************************************
    //**** Moves one single vertex
    //***********************************************************************************
    _move_connected_vertex(ev, vertex) {

        let start_wall = vertex.walls.find(w => w.locked);
        let mouse_position = ev.mouse_world;
        if (start_wall) {
            let dir = start_wall.direction_to(vertex);
            let x = cn_dot(dir,cn_sub(mouse_position,vertex.position));
            mouse_position = cn_add(vertex.position,cn_mul(dir,x));
        }

        var snap = new cn_snap(mouse_position, ev.camera.snap_world_distance, null, ev.camera);
        if (start_wall) {
            snap.freedoms = 1;
            snap.direction = start_wall.direction_to(vertex);
        }

        this.initial_snap_lines.forEach(sl => snap.check_line(sl[0],sl[1]));

        vertex.walls.forEach(w => {
            const other_vertex = w.other_vertex(vertex);
            snap.previous_point = other_vertex.position;
            this._connected_walls.filter(ww => ww.vertices.includes(other_vertex) || ww.supported_vertices.includes(other_vertex) || ww.vertices[0].support_wall == w || ww.vertices[1].support_wall == w).forEach(ww => {
                snap.check_angles(ww.vertex_position(0), ww.vertex_position(1), 45);
            });
            const ww = (w == vertex.walls[0]) ? vertex.walls[1] : vertex.walls[0];
            snap.check_angles(ww.vertex_position(0), ww.vertex_position(1), 45);
        });

        //*** snap on background map */
        snap.snap_elements = this._scene.storey.background_maps;
        snap.snap_point();

        //*** We treat the snap */
        this.push_item_set(vertex, "position");
        vertex.position = snap.position;

        this._svg = snap.svg;

        //*** Final changes */
        this._pending_changes = () => { };

        return true;
    }

    //***********************************************************************************
    //**** Moves one single vertex
    //***********************************************************************************
    _move_single_vertex(ev, vertex) {

        var start_wall = vertex.walls[0];
        var wid = start_wall.vertices.indexOf(vertex);

        let mouse_position = ev.mouse_world;
        if (start_wall.locked) {
            let dir = start_wall.direction_to(vertex);
            let x = cn_dot(dir,cn_sub(mouse_position,vertex.position));
            mouse_position = cn_add(vertex.position,cn_mul(dir,x));
        }

        var snap = new cn_snap(mouse_position, ev.camera.snap_world_distance, start_wall.vertex_position(1 - wid), ev.camera);

        if (start_wall.locked) {
            snap.freedoms = 1;
            snap.direction = start_wall.direction_to(vertex);
        }
        //*** Try to find a snap vertex */
        
        if (!start_wall.locked) {
            snap.vertex = this._scene.vertices.find(v =>
                v.is_free() &&
                v != vertex &&
                v.walls.length == 1 &&
                v.walls[0].wall_type.constructor == start_wall.wall_type.constructor &&
                v.walls[0].wall_type.free == start_wall.wall_type.free &&
                cn_manhattan_distance(v.position, snap.position) < snap.range
            );
            if (snap.vertex) {
                snap.freedoms = 0;
                snap.position = snap.vertex.position;
            }
        }

        this.initial_snap_lines.forEach(sl => snap.check_line(sl[0],sl[1]));

        //*** Try to find a support position */
        let mouse_wall = null;
        const dir = cn_sub(snap.position, snap.previous_point);
        const max_distance = cn_normalize(dir) + snap.range;
        const shape_impact = this._scene.raytrace_support_shape(snap.previous_point, dir, max_distance, start_wall.vertices[1 - wid]);
        if (shape_impact && shape_impact.distance >= max_distance - 2 * snap.range)
            mouse_wall = shape_impact.wall;
        else
            mouse_wall = this._scene.find_wall(snap.position, ev.camera.snap_world_distance, [start_wall]);
        if (mouse_wall)
            snap.find_wall_support(mouse_wall);

        //*** snap on implied wall directions */
        this._connected_walls.forEach(w => snap.check_angles(w.vertex_position(0), w.vertex_position(1), 45));

        //*** We treat the snap */
        this.push_item_set(vertex, "position");
        vertex.position = snap.position;
        vertex.set_support_wall(snap.wall, snap.wall_side, this._scene.building.transaction_manager);

        this._svg = snap.svg;

        this._mouse_position = cn_add(this._mouse_position, cn_sub(snap.position, vertex.position));

        //*** Final changes */
        this._pending_changes = () => {
            if (snap.vertex) {
                this.push_item_set(start_wall, "vertices");
                start_wall.vertices[wid] = snap.vertex;
                vertex.walls = [];
                this._scene.remove_vertex(vertex, this._scene.building.transaction_manager);
                this._controller.select_element(snap.vertex);
                this.on_selection_change();
            }
        };

        return true;
    }

    //***********************************************************************************
    //**** Update mouseover elements
    //***********************************************************************************
    update_mouseover(ev) {
        this.clear_move();

        if (this._implied_wall_measures.some(m => m.move(ev)))
            return true;

        this._mouse_position = cn_clone(ev.mouse_world);

        //*** mouseover priority on selected vertex */
        var can_translate_vertex = this.can_translate_vertex();
        if (can_translate_vertex) {
            var vtx = this._controller.get_selection()[0];
            if (vtx.contains(ev.mouse_world, ev.camera.snap_world_distance * 3)) {
                this._mouseover_element = vtx;
                return true;
            }
        }

        //*** maybe over angular measure */
        this._mouseover_angle = this.angle_sensitive_areas.find(sa => sa.on_point(ev.mouse_screen));
        if (this._mouseover_angle) {
            this._mouseover_angle.mouseover = true;
            return true;
        }

        this._mouseover_element = this._controller.find_element(ev.mouse_world, ev.camera.snap_world_distance);

        //*** orthogonal measure from wall  */
        if (this._mouseover_element && this._mouseover_element.constructor == cn_wall) {
            this._mouse_wall_measures = {};
            this._mouse_wall_measures.wall = this._mouseover_element;
            this._mouse_wall_measures.point = cn_clone(ev.mouse_world);
            this.update_wall_measures(this._mouse_wall_measures);
        }

        //*** mouseover priority on selected wall */
        var can_translate_walls = this.can_translate_walls();
        if (can_translate_walls) {
            // @ts-ignore
            var wall = (this._mouseover_element && this._mouseover_element.constructor == cn_wall) ? this._mouseover_element : null;
            if (wall && this._controller.get_selection().indexOf(wall) >= 0) {
                return true;
            }
        }

        //** If translation is allowed, mouse over a translation measure ? */
        if (can_translate_walls || can_translate_vertex) {
            //*** Maybe mouse over a measure ?
            this._measures.forEach(m => m.clear_move());
            this._mouseover_measure = this._measures.find(m => m.mouseover(ev.mouse_screen));
            if (this._mouseover_measure) return true;

            //*** maybe mouse over a selection measure */
            if (this._selection_wall_measures) {
                for (var i in this._selection_wall_measures.measures) {
                    var mwm = this._selection_wall_measures.measures[i];
                    if (Math.abs(mwm[2][0] - ev.mouse_screen[0]) > 30) continue;
                    if (Math.abs(mwm[2][1] - ev.mouse_screen[1]) > 10) continue;
                    this._mouseover_selection_measure = { wall: this._selection_wall_measures.wall, index: i };
                    return true;
                }
            }
        }

        //*** maybe mouse over space measure ? */
        if (this._selected_space && !this._selected_space.outside && this._space_diagonals)
            this._mouseover_space = this._selected_space.measure.is_mouseover(ev.mouse_world, ev.mouse_screen, ev.camera);

        return this._mouseover_space;
    }

    //***********************************************************************************
    //**** Compute wall measures
    //***********************************************************************************

    update_wall_measures(wm) {
        wm.measures = [];
        var wall = wm.wall;
        var position = wall.compute_abcissa(wm.point);
        var p = wall.compute_axis_position_from_abcissa(position);
        for (var niter = 0; niter < 2; niter++) {
            let origin = cn_add(p, cn_mul(wall.bounds.normal, (niter == 0) ? wall.bounds.y0 : wall.bounds.y1));
            const direction = (niter == 0) ? cn_mul(wall.bounds.normal, -1) : wall.bounds.normal;
            var impact = this._scene.raytrace_spaces(cn_add(origin, cn_mul(direction, 0.01)), direction);
            if (impact)
                wm.measures.push([origin, impact.point, [0, 0]]);
        }
    }

    //***********************************************************************************
    /**
     * Returns true if translation is allowed with given selection
     * @returns {boolean}
     */
    can_translate() {
        if (this.can_translate_vertex()) return true;
        if (this.can_translate_walls()) return true;
        return false;
    }

    //***********************************************************************************
    /**
     * Returns true if translation is allowed with given selection
     * @returns {cn_vertex} returns selected vertex
     */
    can_translate_vertex() {
        var selection = this._controller.get_selection();
        if (selection.length != 1) return null;
        if (selection[0].constructor != cn_vertex) return null;
        if (selection[0].walls.length > 1 && !selection[0].walls.some(w => !w.locked)) return null;
        return selection[0];
    }

    //***********************************************************************************
    /**
     * Returns true if translation is allowed with given selection
     * @returns {boolean}
     */
    can_translate_walls() {
        var selection = this._controller.get_selection();
        if (selection.length != 1) return null;
        if (selection[0].constructor != cn_wall) return null;
        return !selection[0].locked;
        return (!this._implied_walls.some(w => {
            if (w.locked) return true;
            return [...w.vertices, ...w.supported_vertices].some(vertex => vertex.walls.some(ww => ww.locked));
        }));
    }

    //***********************************************************************************
    //**** Apply translation to current selection
    //***********************************************************************************
    apply_wall_translation(actual_delta, transaction_name, camera = null, display_log = false) {

        if (!this.can_translate_walls()) return false;

        if (transaction_name != "") {
            this.push_transaction(transaction_name);
        }
        var normalized_delta = cn_clone(actual_delta);
        var delta_size = cn_normalize(normalized_delta);
        var actual_distance = delta_size;
        if (actual_distance == 0) return false;

        let wall_to_remove = null;

        //*** First step : we compute actual displacement distance, depending on snap */
        if (camera) {
            let max_distance = actual_distance + camera.snap_world_distance;
            let min_distance = actual_distance - camera.snap_world_distance;
            this._implied_vertices.forEach(v => {
                if (v.walls.length == 2) {
                    const i0 = this._implied_walls.includes(v.walls[0]);
                    const i1 = this._implied_walls.includes(v.walls[1]);
                    if (i0 != i1 && !v.walls[0].parallel_to(v.walls[1])) {
                        const ii = (i0) ? 0 : 1;
                        let remove_distance = cn_dot(normalized_delta, cn_sub(v.walls[1 - ii].other_vertex(v).position, v.position));
                        if (remove_distance > min_distance && remove_distance < max_distance) {
                            wall_to_remove = v.walls[1 - ii];
                            actual_distance = remove_distance;
                            max_distance = remove_distance;
                        }
                    }
                }
                else if (this._sliding_vertices.includes(v)) {
                    const wall = v.walls[0];
                    const v1 = cn_add(wall.other_vertex(v).position, actual_delta);
                    const sliding_wall = v["initial_support_wall"];
                    const impact = (sliding_wall)?sliding_wall.raytrace_support_shape(v1, wall.direction_to(v)):null;
                    if (impact) {
                        const snap = new cn_snap(impact.point, camera.snap_world_distance, null, camera);
                        snap.thickness = wall.wall_type.thickness / Math.abs(cn_dot(wall.bounds.direction, sliding_wall.bounds.normal));
                        snap.axis = wall.axis;
                        if (wall.axis != CN_MIDDLE && wall.vertices[0] == v)
                            snap.axis = (wall.axis == CN_OUTER) ? CN_INNER : CN_OUTER;
                        snap.find_wall_support(sliding_wall);
                        if (snap.freedoms == 0) {
                            const distance = cn_dot(normalized_delta, cn_sub(snap.position, v.position));
                            if (distance < max_distance) {
                                actual_distance = distance;
                                max_distance = distance;
                                wall_to_remove = null;
                            }
                        }
                    }
                }
            });
            if (max_distance <= 0) return false;
        }
        actual_delta[0] = normalized_delta[0] * actual_distance;
        actual_delta[1] = normalized_delta[1] * actual_distance;

        //*** second step : actual translation */
        const delta = cn_mul(normalized_delta, actual_distance);
        this._implied_vertices.forEach(v => {
            this.push_item_set(v, "position");
            const previous_position = v.position;
            v.position = cn_add(v.position, delta);
            if (v.walls.length == 2) {
                const i0 = this._implied_walls.includes(v.walls[0]);
                const i1 = this._implied_walls.includes(v.walls[1]);
                if (i0 != i1) {
                    const ii = (i0) ? 0 : 1;
                    //*** if walls are parallel, add one wall */
                    if (Math.abs(cn_dot(v.walls[ii].bounds.direction, v.walls[1 - ii].bounds.direction)) > CN_WALL_COSINE_SHAPE_THRESHOLD) {
                        const new_vertex = new cn_vertex(previous_position, false, this._scene);
                        const new_wall = new cn_wall(v, new_vertex, v.walls[ii].wall_type, v.walls[ii].axis, this._scene);
                        this.push_item_set(this._scene, ["vertices", "walls"]);
                        this._scene.walls.push(new_wall);
                        this._scene.vertices.push(new_vertex);
                        this._implied_walls.push(new_wall);
                        this._managed_walls.push(new_wall);
                        this.push_item_set(v.walls[1 - ii], ["vertices"]);
                        const vertex_index = v.walls[1 - ii].vertex_index(v);
                        v.walls[1 - ii].vertices[vertex_index] = new_vertex;
                        new_vertex.walls = [v.walls[1 - ii], new_wall]
                        v.walls[1 - ii] = new_wall;
                    }
                    else {
                        const p = cn_intersect_line(v.position, v.walls[ii].bounds.direction, v.walls[1 - ii].other_vertex(v).position, v.walls[1 - ii].bounds.direction);
                        if (p) v.position = p;

                        //*** check if non implied wall changes direction */
                        if (cn_dot(v.walls[1 - ii].bounds.direction, cn_sub(v.walls[1 - ii].vertices[1].position, v.walls[1 - ii].vertices[0].position)) < 0.001) {

                            const wall = this._scene.find_wall(v.position, 0.01, [...v.walls, this._scene.walls.filter(w => w.parallel_to(v.walls[ii]))]);
                            if (wall) {
                                let removed_wall = v.walls[1 - ii];
                                this._scene.remove_wall(v.walls[1 - ii], this._building.transaction_manager);
                                let vi = this._managed_walls.indexOf(removed_wall);
                                if (vi >= 0) this._managed_walls.splice(vi, 1);
                                v.check_support_wall(wall, this._scene.building.transaction_manager);
                                if (!this._sliding_vertices.includes(v))
                                    this._sliding_vertices.push(v);
                            }
                        }
                    }
                }
            }
            else if (this._sliding_vertices.includes(v)) {
                let supporting_wall = v.walls[0];
                if (!v.check_support_wall(v["initial_support_wall"], this._scene.building.transaction_manager)) {
                    let ray_direction = cn_clone(supporting_wall.bounds.normal);
                    let reverse = false;
                    if (cn_dot(ray_direction, delta) > 0) {
                        ray_direction = cn_mul(ray_direction, -1);
                        reverse = true;
                    }
                    let ray_origin = cn_clone(v.position);
                    let wall_dir = supporting_wall.direction_to(v);
                    let xx = supporting_wall.wall_type.thickness;
                    if (supporting_wall.axis == CN_MIDDLE)
                        xx *= 0.5;
                    else {
                        let outer_normal = (supporting_wall.axis == CN_OUTER) ? supporting_wall.bounds.normal : cn_mul(supporting_wall.bounds.normal, -1);
                        if (cn_dot(outer_normal, ray_direction) > 0) xx = 0;
                    }
                    ray_origin = cn_add(ray_origin, cn_mul(wall_dir, xx));
                    let max_distance = cn_normalize(ray_direction);
                    let impact = this._scene.raytrace_walls(ray_origin, ray_direction, max_distance * 2, v, v.walls[0]);
                    if (impact) {
                        const new_vertex = new cn_vertex(impact.point, false, this._scene);
                        let wv = [v, new_vertex];
                        let vertex_index = supporting_wall.vertex_index(v);
                        const new_wall = new cn_wall(wv[1 - vertex_index], wv[vertex_index], supporting_wall.wall_type, supporting_wall.axis, this._scene);
                        this.push_item_set(this._scene, ["vertices", "walls"]);
                        this._scene.walls.push(new_wall);
                        this._scene.vertices.push(new_vertex);
                        this._managed_walls.push(new_wall);
                        new_vertex.support_wall = impact.wall;
                        new_vertex.walls = [new_wall];
                        this.push_item_set(v, ["walls", "support_wall", "position"]);
                        v.walls.push(new_wall);
                        v.support_wall = null;
                        v.position = ray_origin;
                    }
                }
            }
        });

        //*** manage side effects */
        this._scene.scene_analyser.update_tmp_walls(this._managed_walls);

        this._supported_vertices.forEach(v => {
            v.check_support_wall(v["initial_support_wall"], this._scene.building.transaction_manager);
        });

        this._scene.scene_analyser.update_tmp_walls(this._connected_walls);
        this._update_measures();

        this._sliding_vertices.filter(v => v.support_wall).forEach(v => v.support_wall.update_openings());

        this._pending_changes = () => { };

        return true;
    }

    //***********************************************************************************
    //**** remove selection
    //***********************************************************************************
    remove_selection() {
        var first_time = true;
        var scene = this._scene;

        //*** then remove walls
        for (var i in this._selection) {
            if (this._selection[i].constructor != cn_wall) continue;
            var wall = this._selection[i];
            if (first_time) {
                const transaction_name = (wall.balcony) ? 'Suppression de balcon' : 'Suppression de mur';
                this.push_transaction('Suppression de mur', '', () => {
                    scene.full_update();
                });
                first_time = false;
            }

            this.push_item_set(wall, [], w => {
                if (scene.walls.indexOf(w) >= 0)
                    scene.remove_wall(w);
                else
                    scene.insert_wall(w);
            });
            this._scene.remove_wall(wall);
        }
        scene.full_update();
        this._selection = [];
        this._mouseover_element = null;
        this.clear_selection();
        this.call('selection_change');
    }
}

